import React, { useRef, useState, useCallback } from 'react';
import { uploadProfilePicture } from '../lib/storage';
import { useAppStore } from '../store/appStore';
import { ImageCropModal } from './ImageCropModal';

interface ProfileImageUploadProps {
  currentImageUrl: string | null;
  accountId: string;
  onUploadComplete?: (url: string) => void;
}

export const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  currentImageUrl,
  accountId,
  onUploadComplete,
}) => {
  const { updateAccount } = useAppStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayImage = preview || currentImageUrl;
  const hasChanges = !!preview;

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Velg en bildefil.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Bildet må være under 10MB.');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropSrc(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, []);

  const handleCropDone = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    setPreview(url);
    setPendingBlob(blob);
    setCropSrc(null);
  };

  const handleCropCancel = () => {
    setCropSrc(null);
  };

  const handleSave = async () => {
    if (!pendingBlob) return;
    setIsSaving(true);
    setError(null);
    try {
      const file = new File([pendingBlob], 'profile.jpg', { type: 'image/jpeg' });
      const url = await uploadProfilePicture(file, accountId);
      await updateAccount(accountId, { profileImageUrl: url });
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setPendingBlob(null);
      onUploadComplete?.(url);
    } catch {
      setError('Opplasting feilet. Prøv igjen.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setPendingBlob(null);
    setError(null);
  };

  const handleRemove = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await updateAccount(accountId, { profileImageUrl: null });
      setPreview(null);
      setPendingBlob(null);
    } catch {
      setError('Kunne ikke fjerne bilde.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          onCrop={handleCropDone}
          onCancel={handleCropCancel}
        />
      )}

      <div className="flex flex-col items-center gap-3">
        <div
          className="relative group cursor-pointer select-none"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !hasChanges && fileInputRef.current?.click()}
          title={hasChanges ? undefined : 'Klikk eller dra for å laste opp bilde'}
        >
          <div
            className={`w-[100px] h-[100px] rounded-full overflow-hidden border-2 transition-all duration-200 ${
              isDragging
                ? 'border-white shadow-[0_0_20px_rgba(255,255,255,0.3)] scale-105'
                : hasChanges
                ? 'border-white/60'
                : 'border-white/10 group-hover:border-white/50'
            }`}
          >
            {displayImage ? (
              <img
                src={displayImage}
                alt="Profilbilde"
                className="w-full h-full object-cover bg-[#2a2a2a]"
              />
            ) : (
              <div className="w-full h-full bg-[#2a2a2a] flex items-center justify-center text-gray-500 text-2xl font-bold">
                {/* Placeholder initial */}
              </div>
            )}
            {!hasChanges && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {error && <p className="text-red-400 text-xs text-center">{error}</p>}

        {hasChanges ? (
          <div className="flex gap-2">
            <button onClick={() => fileInputRef.current?.click()} disabled={isSaving} className="text-xs px-3 py-1.5 rounded-md bg-[#2a2a2a] hover:bg-[#333] text-white/60 hover:text-white transition-colors disabled:opacity-50">
              Beskjær på nytt
            </button>
            <button onClick={handleSave} disabled={isSaving} className="text-xs px-3 py-1.5 rounded-md bg-white hover:bg-gray-200 text-black font-medium transition-colors disabled:opacity-50">
              {isSaving ? 'Lagrer...' : 'Lagre bilde'}
            </button>
            <button onClick={handleDiscard} disabled={isSaving} className="text-xs px-3 py-1.5 rounded-md bg-[#2a2a2a] hover:bg-[#333] text-white/70 hover:text-white transition-colors disabled:opacity-50">
              Forkast
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => fileInputRef.current?.click()} className="text-xs px-3 py-1.5 rounded-md bg-[#2a2a2a] hover:bg-[#333] text-white/60 hover:text-white transition-colors">
              {currentImageUrl ? 'Bytt bilde' : 'Last opp bilde'}
            </button>
            {currentImageUrl && (
              <button onClick={handleRemove} disabled={isSaving} className="text-xs px-3 py-1.5 rounded-md bg-[#2a2a2a] hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors disabled:opacity-50">
                Fjern
              </button>
            )}
          </div>
        )}

        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />
      </div>
    </>
  );
};
